import { useEffect, useRef } from "react";
import { watchAllRequestsForCounts } from "../services/requests";
import { getNextMeetingsAsync, toIsoDate } from "../utils/dateHelpers";

const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;

function notify(title, body) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  try {
    new Notification(title, { body, icon: "/logo192.png" });
  } catch (error) {
    console.warn("⚠️ Impossible d'envoyer la notification:", error);
  }
}

async function ensureNotificationPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission === "default") {
    try {
      await Notification.requestPermission();
    } catch (error) {
      console.warn("⚠️ Permission notification refusée ou indisponible:", error);
    }
  }
}

function getMeetingState(meetingAt) {
  const baseKey = `cvtrg_pending_notif_${meetingAt}`;
  const j2Sent = localStorage.getItem(`${baseKey}_j2`) === "1";
  const rawLastCount = localStorage.getItem(`${baseKey}_last_count`);
  const lastCount = rawLastCount == null ? null : Number(rawLastCount);
  return { baseKey, j2Sent, lastCount };
}

function setMeetingState(baseKey, { j2Sent, lastCount }) {
  if (j2Sent != null) {
    localStorage.setItem(`${baseKey}_j2`, j2Sent ? "1" : "0");
  }
  if (lastCount != null && Number.isFinite(lastCount)) {
    localStorage.setItem(`${baseKey}_last_count`, String(lastCount));
  }
}

function clearOldMeetingState(currentMeetingAt) {
  if (typeof window === "undefined") return;
  const prefix = "cvtrg_pending_notif_";
  for (const key of Object.keys(localStorage)) {
    if (!key.startsWith(prefix)) continue;
    if (currentMeetingAt && key.includes(`_${currentMeetingAt}_`)) continue;
    localStorage.removeItem(key);
  }
}

export default function PendingRequestsNotifier() {
  const initializedRef = useRef(false);

  useEffect(() => {
    let active = true;
    ensureNotificationPermission();

    const unsubscribe = watchAllRequestsForCounts(async (rows) => {
      if (!active) return;

      try {
        const meetings = await getNextMeetingsAsync(8);
        const now = Date.now();
        const nextMeeting = (meetings || [])
          .filter((m) => typeof m?.meetingAt === "number" && m.meetingAt > now)
          .sort((a, b) => a.meetingAt - b.meetingAt)[0];

        if (!nextMeeting?.meetingAt) return;

        const meetingAt = nextMeeting.meetingAt;
        const meetingIso = nextMeeting.isoDate || toIsoDate(meetingAt);
        const windowStart = meetingAt - TWO_DAYS_MS;
        const inTwoDaysWindow = now >= windowStart && now < meetingAt;

        clearOldMeetingState(meetingAt);

        const pendingForNextMeeting = (Array.isArray(rows) ? rows : []).filter((r) => {
          const requestIso = r.isoDate || (r.meetingAt ? toIsoDate(r.meetingAt) : null);
          return requestIso === meetingIso;
        });

        const currentCount = pendingForNextMeeting.length;
        const { baseKey, j2Sent, lastCount } = getMeetingState(meetingAt);

        // Notification "rappel à J-2" (une seule fois, seulement s'il y a au moins une demande en attente)
        if (inTwoDaysWindow && currentCount > 0 && !j2Sent) {
          notify(
            "Rappel prochaine réunion",
            `${currentCount} demande${currentCount > 1 ? "s" : ""} en attente pour la prochaine réunion.`,
          );
          setMeetingState(baseKey, { j2Sent: true });
        }

        // Notification "nouvelle demande entre J-2 et la réunion"
        if (inTwoDaysWindow) {
          if (initializedRef.current && lastCount != null && currentCount > lastCount) {
            const delta = currentCount - lastCount;
            notify(
              "Nouvelle demande en attente",
              `${delta} nouvelle${delta > 1 ? "s" : ""} demande${delta > 1 ? "s" : ""} pour la prochaine réunion.`,
            );
          }
          setMeetingState(baseKey, { lastCount: currentCount });
        } else {
          // Hors fenêtre J-2: on prépare une baseline sans notifier.
          setMeetingState(baseKey, { lastCount: currentCount });
        }

        initializedRef.current = true;
      } catch (error) {
        console.error("❌ [PendingRequestsNotifier] Erreur:", error);
      }
    });

    return () => {
      active = false;
      unsubscribe && unsubscribe();
    };
  }, []);

  return null;
}
